<template>
  <div class="users content-container flex-column">
    <div class="content-wrap">

      <!-- Widok dodawania nowego użytkownika -->
      <div v-if="showAddUser" @cancel="showAddUser = false" class="d-flex users-section-container">
        <div class="users-section">
          <span>
            {{ t('add-user') }}
          </span>
        </div>
        <div class="content-wrap">
          <div class="collapse show" id="addUser">
            <!-- Komponent do dodawania użytkownika.
                 Nasłuchuje eventów: zamknięcie widoku lub dodanie użytkownika -->
            <AddUser @user-added="handleUserAdded" @cancel-add-user="showAddUser = false" />
          </div>
        </div>
      </div>

      <!-- Widok listy użytkowników (główna lista, jeśli nie dodajemy) -->
      <div v-else class="d-flex users-section-container">
        <div class="users-section">
          <span>
            {{ t('user-list') }}
          </span>
        </div>
        <div class="content-wrap">
          <!-- Pasek nagłówka listy użytkowników + wyszukiwarka + przycisk dodawania -->
          <div v-if="!isEditMode" class="d-flex align-items-center justify-content-between user-list-header">
            <h3>{{ t('user-list').toUpperCase() }}</h3>
            <div class="search-bar">
              <!-- Filtr: przynależność do grupy list poszukiwanych (blacklist/whitelist) -->
              <select
                v-if="showWantedGroupFilter"
                v-model="selectedWantedGroupId"
                class="wanted-group-filter"
              >
                <option value="">{{ t('all-wanted-list-groups') }}</option>
                <option
                  v-for="group in wantedGroups"
                  :key="group.id"
                  :value="group.id"
                >
                  {{ group.name }}
                </option>
              </select>
              <!-- Wyszukiwarka po użytkownikach -->
              <input
                type="text"
                v-model="searchQuery"
                :placeholder="t('search-user')"
              />
              <!-- Przycisk + do wyświetlenia formularza dodawania użytkownika (po prawej) -->
              <span @click="toggleAddUser" class="add-user-action" :title="t('add-user')">+</span>
            </div>
          </div>
          <!-- Lista użytkowników (przekazuje searchQuery do filtrowania) -->
          <div class="collapse show" id="userList">
            <UserList
              :search-query="searchQuery"
              :wanted-group-id="selectedWantedGroupId"
              @clearSearch="searchQuery = ''"
              @editMode="isEditMode = $event"
              @wantedGroupsLoaded="wantedGroups = $event"
              :key="userListKey"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useI18n } from "vue-i18n";
import UserList from "@/components/UserList";
import AddUser from "@/components/AddUser";
import Users from "@/services/Users";
import moment from "moment";
import { isSG } from "@/config/appVersion";

export default {
  name: "HomeView",
  components: {
    UserList,
    AddUser,
  },
  data() {
    const { t } = useI18n();
    return {
      isDownloading: false, // status pobierania pliku
      searchQuery: '',      // fraza wyszukiwania użytkownika
      selectedWantedGroupId: '', // wybrana grupa list poszukiwanych do filtrowania ('' = wszystkie)
      wantedGroups: [],     // dostępne grupy list poszukiwanych (zasilane z UserList)
      userListKey: 0,       // klucz do wymuszenia reloadu listy użytkowników
      showAddUser: false,   // czy widok dodawania użytkownika jest widoczny
      isEditMode: false,    // czy edytujemy konkretnego użytkownika (UserList ustawia ten tryb)
      t
    };
  },
  computed: {
    // Filtr grup list poszukiwanych pokazujemy we wszystkich wariantach SG (SG/NOSG/POSG) —
    // tak jak kolumnę „Grupy poszukiwanych". W pozostałych (TEST, lokalny dev) select jest ukryty.
    showWantedGroupFilter() {
      return isSG && this.wantedGroups.length > 0;
    },
  },
  methods: {
    // Przełącza formularz dodawania użytkownika
    toggleAddUser() {
      this.showAddUser = !this.showAddUser;
    },
    // Pobranie logów użytkowników do ZIP-a (ostatnie 7 dni)
    retrieveZip() {
      this.isDownloading = true;
      const dateFrom = moment().subtract(7, "days").toISOString(false);
      const dateTo = moment().toISOString(false);
      Users.getZip(dateFrom, dateTo)
        .then((data) => {
          Users.fetch(data.link)
            .then((response) => {
              const link = document.createElement("a");
              link.href = URL.createObjectURL(response);
              link.download = "user_logs";
              link.click();
              this.isDownloading = false;
              URL.revokeObjectURL(link.href);
            })
            .catch(console.error);
        })
        .catch((error) => {
          this.loading = false;
          console.error(error);
        });
    },
    // Po dodaniu użytkownika ukrywa formularz i odświeża listę użytkowników
    handleUserAdded() {
      this.showAddUser = false;
      this.userListKey++;
    }
  },
};
</script>

<style scoped>
.users-section-container {
  width: 100%;
}

.user-list-header {
  padding-top: .5rem;
  background-color: var(--surface-primary);
  border-bottom: 1px solid var(--border-default);
}

.search-bar {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding-right: 1rem;
  flex-wrap: nowrap;
}

/* Wspólny wygląd wyszukiwarki i selecta filtra grup — identyczne wymiary i obramowanie */
.search-bar input,
.search-bar .wanted-group-filter {
  margin: 0;
  height: var(--control-height);
  padding: 6px 12px;
  border: 1px solid var(--border-default);
  border-radius: var(--control-radius);
  font-size: 0.95rem;
  line-height: 1.2;
  color: var(--text-primary);
  background-color: var(--surface-primary);
  box-sizing: border-box;
  vertical-align: middle;
}

/* Szersze pole — placeholder wymienia trzy kryteria wyszukiwania
   („Szukaj: login, imię, nazwisko..."), przy 220 px był ucinany. */
.search-bar input {
  width: 280px;
}

/* Select filtra grup — czytelny, natywna strzałka rozwijania */
.search-bar .wanted-group-filter {
  width: 260px;
  cursor: pointer;
  text-overflow: ellipsis;
}

.users-section {
  width: var(--sidebar-width);
  background: var(--surface-secondary);
}

.users-section > span {
  padding: var(--spacing-md) var(--spacing-lg);
  position: relative;
  display: block;
}

.icon-collapse {
  background: url("../assets/icons/zwin.png") no-repeat left top;
  width: 19px;
  height: 19px;
  position: absolute;
  cursor: pointer;
  right: 10px;
}

.collapsed.icon-collapse {
  background: url("../assets/icons/rozwin.png") no-repeat left top;
}

.content-wrap h3 {
  padding: 15px 25px;
  background-color: var(--surface-primary);
}

.add-user-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: var(--control-height);
  cursor: pointer;
  font-size: 28px;
  line-height: 1;
  /* color: var(--status-warn-strong);
  border: 1px solid var(--border-default); */
  border-radius: var(--control-radius);
  background-color: var(--surface-primary);
  user-select: none;
}

/* .add-user-action:hover {
  background-color: var(--status-warn-strong);
  color: var(--text-inverse);
  border-color: var(--status-warn-strong);
} */
</style>
